/**
 * Gift Wrap functionality for cart items
 * Adds/removes gift wrapping (20 DKK) for individual cart items
 */

class GiftWrapManager {
  constructor() {
    // Singleton - prevent duplicate instances and event listeners
    if (window._giftWrapManagerInstance) {
      return window._giftWrapManagerInstance;
    }
    window._giftWrapManagerInstance = this;

    this.giftWrapVariantId = null;
    this.isProcessing = false; // Prevent double-clicks
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleCheckboxChange.bind(this));
  }

  async getGiftWrapVariantId() {
    if (this.giftWrapVariantId) {
      return this.giftWrapVariantId;
    }

    try {
      const response = await fetch('/products/gift-wrapping.js');
      if (!response.ok) {
        console.error('Gift wrap product not found');
        return null;
      }
      const product = await response.json();
      this.giftWrapVariantId = product.variants[0].id;
      return this.giftWrapVariantId;
    } catch (error) {
      console.error('Error fetching gift wrap product:', error);
      return null;
    }
  }

  async handleCheckboxChange(event) {
    const checkbox = event.target.closest('.gift-wrap-input');
    if (!checkbox) return;

    const container = checkbox.closest('.gift-wrap-checkbox');
    if (!container) return;

    // Prevent rapid clicks
    if (this.isProcessing) {
      event.preventDefault();
      return;
    }

    const parentKey = container.dataset.parentKey;
    const parentVariantId = container.dataset.parentVariantId;
    const parentQuantity = parseInt(container.dataset.parentQuantity, 10);
    const parentTitle = container.dataset.parentTitle;

    if (!parentKey) return;

    this.isProcessing = true;
    container.classList.add('loading');

    try {
      if (checkbox.checked) {
        const giftWrapVariantId = await this.getGiftWrapVariantId();
        if (!giftWrapVariantId) {
          throw new Error('Could not get gift wrap variant ID');
        }
        await this.addGiftWrap(giftWrapVariantId, parentKey, parentVariantId, parentQuantity, parentTitle);
      } else {
        await this.removeGiftWrapForParent(parentKey);
      }

      // Small delay before triggering cart update to let Shopify process
      await new Promise(resolve => setTimeout(resolve, 200));
      document.dispatchEvent(new CustomEvent('updateCart'));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap' });
    } catch (error) {
      console.error('Gift wrap error:', error);
      checkbox.checked = !checkbox.checked;
    } finally {
      container.classList.remove('loading');
      // Allow new clicks after a delay
      setTimeout(() => {
        this.isProcessing = false;
      }, 500);
    }
  }

  async addGiftWrap(variantId, parentKey, parentVariantId, quantity, parentTitle) {
    // Use queue if available
    const doAdd = async () => {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: quantity,
            properties: {
              '_parent_key': parentKey,
              '_parent_variant_id': parentVariantId,
              'Gaveindpakning til': parentTitle
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add gift wrap');
      }

      // Invalidate cart cache after mutation
      if (window.CartRequestManager) {
        window.CartRequestManager.invalidateCache();
      }

      return response.json();
    };

    if (window.CartRequestManager) {
      return window.CartRequestManager.queueRequest(doAdd);
    }
    return doAdd();
  }

  async removeGiftWrapForParent(parentKey) {
    const cart = await this.getCart();
    const giftWrapItem = cart.items.find(item =>
      item.properties && item.properties['_parent_key'] === parentKey
    );

    if (giftWrapItem) {
      await this.updateItemQuantity(giftWrapItem.key, 0);
    }
  }

  async syncGiftWrapQuantity(parentKey, newQuantity) {
    const cart = await this.getCart();
    const giftWrapItem = cart.items.find(item =>
      item.properties && item.properties['_parent_key'] === parentKey
    );

    if (giftWrapItem && giftWrapItem.quantity !== newQuantity) {
      if (newQuantity === 0) {
        await this.updateItemQuantity(giftWrapItem.key, 0);
      } else {
        await this.updateItemQuantity(giftWrapItem.key, newQuantity);
      }
    }
  }

  async updateItemQuantity(itemKey, quantity) {
    // Use queue if available
    const doUpdate = async () => {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          updates: {
            [itemKey]: quantity
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      // Invalidate cart cache after mutation
      if (window.CartRequestManager) {
        window.CartRequestManager.invalidateCache();
      }

      return response.json();
    };

    if (window.CartRequestManager) {
      return window.CartRequestManager.queueRequest(doUpdate);
    }
    return doUpdate();
  }

  async getCart() {
    // Use CartRequestManager cache if available
    if (window.CartRequestManager) {
      return window.CartRequestManager.getCart();
    }
    const response = await fetch('/cart.js');
    return response.json();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GiftWrapManager());
} else {
  new GiftWrapManager();
}

// Export for use in WI_cart.js
window.GiftWrapManager = GiftWrapManager;

// Helper function to remove gift wrap when parent is removed
window.removeGiftWrapForParent = async function(parentKey) {
  const manager = window._giftWrapManagerInstance || new GiftWrapManager();
  await manager.removeGiftWrapForParent(parentKey);
};

// Helper function to sync gift wrap quantity when parent quantity changes
window.syncGiftWrapQuantity = async function(parentKey, newQuantity) {
  const manager = window._giftWrapManagerInstance || new GiftWrapManager();
  await manager.syncGiftWrapQuantity(parentKey, newQuantity);
};
